import React from 'react';
import { Button } from '@/components/ui/button';
import { EpisodeStatus } from '@/types/episode.types';
import {
  RefreshCw,
  Archive,
  FileText,
  Check,
  Volume2,
  Combine,
  Send,
  ExternalLink,
  Plus,
  Play,
  Pause,
  Square,
  SkipForward,
  Loader2,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

type IconType = React.ComponentType<{ className?: string }>;

interface PrimaryActionConfig {
  label: string;
  icon: IconType;
  disabled: boolean;
  loading?: boolean;
  variant?: 'default' | 'destructive' | 'success';
}

interface SecondaryActionConfig {
  label: string;
  icon: IconType;
}

interface StatusConfig {
  primary: PrimaryActionConfig;
  secondary: SecondaryActionConfig[];
}

export interface ActionBarProps {
  status: EpisodeStatus;
  onPrimaryAction: () => void;
  onSecondaryAction: (action: string) => void;
  onRefresh: () => void;
  onArchive: () => void;
  onDelete: () => void;
  isArchived: boolean;
  publishedUrl?: string | null;
  clipCount?: number;
  // Playback controls for audio review
  isPlayingAll?: boolean;
  isCurrentlyPlaying?: boolean;
  canGoNext?: boolean;
  onPlayAll?: () => void;
  onPausePlayAll?: () => void;
  onResumePlayAll?: () => void;
  onNextClip?: () => void;
  onStopPlayAll?: () => void;
  // Lock state - disables editing actions but allows playback
  isLocked?: boolean;
}

// ============================================
// STATUS CONFIGURATION
// Matches docs/episode-actionbar-flow.html exactly
// ============================================

const statusConfig: Record<string, StatusConfig> = {
  [EpisodeStatus.DRAFT]: {
    primary: { label: 'Generate Scripts', icon: FileText, disabled: false },
    secondary: [{ label: 'Add Articles', icon: Plus }],
  },
  [EpisodeStatus.GENERATING_SCRIPTS]: {
    primary: { label: 'Generating Scripts...', icon: Loader2, disabled: true, loading: true },
    secondary: [],
  },
  [EpisodeStatus.SCRIPTS_PENDING_REVIEW]: {
    primary: { label: 'Approve All Scripts', icon: Check, disabled: false },
    secondary: [],
  },
  [EpisodeStatus.SCRIPTS_APPROVED]: {
    primary: { label: 'Generate Audio', icon: Volume2, disabled: false },
    secondary: [],
  },
  [EpisodeStatus.GENERATING_AUDIO]: {
    primary: { label: 'Generating Audio...', icon: Loader2, disabled: true, loading: true },
    secondary: [],
  },
  [EpisodeStatus.AUDIO_PENDING_REVIEW]: {
    primary: { label: 'Approve All Audio', icon: Check, disabled: false },
    secondary: [],  // Playback controls handled separately via props
  },
  [EpisodeStatus.AUDIO_APPROVED]: {
    primary: { label: 'Merge Episode', icon: Combine, disabled: false },
    secondary: [],  // Playback controls handled separately via props
  },
  [EpisodeStatus.MERGING]: {
    primary: { label: 'Merging...', icon: Loader2, disabled: true, loading: true },
    secondary: [],
  },
  [EpisodeStatus.MERGED]: {
    primary: { label: 'Publish Episode', icon: Send, disabled: false },
    secondary: [{ label: 'Re-merge', icon: RefreshCw }],  // Play button handled via props
  },
  [EpisodeStatus.FAILED]: {
    primary: { label: 'Retry Failed', icon: RefreshCw, disabled: false, variant: 'destructive' },
    secondary: [],  // No Re-merge - clips need to be fixed first
  },
  [EpisodeStatus.AUDIO_FAILED]: {
    primary: { label: 'Retry Audio', icon: RefreshCw, disabled: false, variant: 'destructive' },
    secondary: [],  // No Re-merge - audio isn't ready
  },
  [EpisodeStatus.SCRIPT_FAILED]: {
    primary: { label: 'Retry Script', icon: RefreshCw, disabled: false, variant: 'destructive' },
    secondary: [],  // No Re-merge - scripts aren't ready
  },
  [EpisodeStatus.PUBLISHING]: {
    primary: { label: 'Publishing...', icon: Loader2, disabled: true, loading: true },
    secondary: [],
  },
  [EpisodeStatus.PUBLISHED]: {
    primary: { label: 'View Episode', icon: ExternalLink, disabled: false, variant: 'success' },
    secondary: [],
  },
  [EpisodeStatus.PUBLISHING_FAILED]: {
    primary: { label: 'Retry Publishing', icon: RefreshCw, disabled: false, variant: 'destructive' },
    secondary: [{ label: 'Re-merge', icon: RefreshCw }],
  },
  [EpisodeStatus.ARCHIVED]: {
    primary: { label: 'Unarchive', icon: Archive, disabled: false },
    secondary: [],
  },
};

// ============================================
// COMPONENT
// ============================================

export const ActionBar: React.FC<ActionBarProps> = ({
  status,
  onPrimaryAction,
  onSecondaryAction,
  onRefresh,
  onArchive,
  onDelete,
  isArchived,
  publishedUrl,
  clipCount = 0,
  // Playback controls
  isPlayingAll = false,
  isCurrentlyPlaying = false,
  canGoNext = false,
  onPlayAll,
  onPausePlayAll,
  onResumePlayAll,
  onNextClip,
  onStopPlayAll,
  // Lock state
  isLocked = false,
}) => {
  let config = statusConfig[status];

  // Special case: DRAFT with no clips - only show "Add Articles" button
  if (status === EpisodeStatus.DRAFT && clipCount === 0) {
    config = {
      primary: { label: 'Add Articles', icon: Plus, disabled: false },
      secondary: [],
    };
  }

  // Fallback for any unmapped statuses
  if (!config) {
    return (
      <div className="flex items-center justify-between py-3 mb-6">
        <span className="text-sm text-muted-foreground">Unknown status: {status}</span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onRefresh} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const { primary, secondary } = config;
  const PrimaryIcon = primary.icon;

  // Primary button uses its own disabled state, plus locked state
  // (The old "disable Generate Scripts when no clips" logic is no longer needed
  // since we now show "Add Articles" as primary when draft with no clips)
  // When locked, disable all editing actions but allow "View Episode" for published
  const isPrimaryDisabled = primary.disabled || (isLocked && status !== EpisodeStatus.PUBLISHED);

  // Determine primary button variant
  const getPrimaryVariant = (): 'default' | 'destructive' | 'outline' => {
    if (primary.variant === 'destructive') return 'destructive';
    if (primary.variant === 'success') return 'default'; // Use default with custom class
    return 'default';
  };

  // Handle primary action - special cases for published and draft with no clips
  const handlePrimaryClick = () => {
    if (status === EpisodeStatus.PUBLISHED && publishedUrl) {
      window.open(publishedUrl, '_blank', 'noopener,noreferrer');
    } else if (status === EpisodeStatus.DRAFT && clipCount === 0) {
      // When no clips, primary button is "Add Articles"
      onSecondaryAction('Add Articles');
    } else {
      onPrimaryAction();
    }
  };

  return (
    <div className="flex items-center justify-between py-6">
      {/* Left side: Primary + Secondary Actions */}
      <div className="flex items-center gap-3">
        {/* Primary Action Button */}
        <Button
          onClick={handlePrimaryClick}
          disabled={isPrimaryDisabled}
          variant={getPrimaryVariant()}
          className={primary.variant === 'success' ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : undefined}
          data-testid="action-bar-primary"
        >
          <PrimaryIcon className={primary.loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {primary.label}
        </Button>

        {/* Secondary Action Buttons / Playback Controls */}
        {(status === EpisodeStatus.AUDIO_PENDING_REVIEW || status === EpisodeStatus.AUDIO_APPROVED) && onPlayAll ? (
          // Playback controls for audio review and approved states (sequence playback)
          !isPlayingAll ? (
            <Button variant="outline" onClick={onPlayAll}>
              <Play className="h-4 w-4" />
              Play All
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={isCurrentlyPlaying ? onPausePlayAll : onResumePlayAll}
              >
                {isCurrentlyPlaying ? (
                  <>
                    <Pause className="h-4 w-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Resume
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={onNextClip}
                disabled={!canGoNext}
              >
                <SkipForward className="h-4 w-4" />
                Next
              </Button>
              <Button
                variant="ghost"
                onClick={onStopPlayAll}
              >
                <Square className="h-4 w-4" />
                Stop
              </Button>
            </>
          )
        ) : status === EpisodeStatus.MERGED && onPlayAll ? (
          // Simple play/pause for merged episode audio
          <Button variant="outline" onClick={isPlayingAll ? onPausePlayAll : onPlayAll}>
            {isPlayingAll ? (
              <>
                <Pause className="h-4 w-4" />
                Pause
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Play
              </>
            )}
          </Button>
        ) : (
          // Standard secondary buttons
          secondary.map((action) => {
            const SecondaryIcon = action.icon;
            return (
              <Button
                key={action.label}
                variant="outline"
                onClick={() => onSecondaryAction(action.label)}
                disabled={isLocked}
              >
                <SecondaryIcon className="h-4 w-4" />
                {action.label}
              </Button>
            );
          })
        )}
      </div>

    </div>
  );
};

export default ActionBar;
